export const formatDocDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear() % 100;
    return `${day}-${month}-${year}`;
  } catch {
    return "";
  }
};

export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "₹0.00";
  const value = parseFloat(amount);
  if (Number.isNaN(value)) return "₹0.00";
  return `₹${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const convertLessThanThousand = (n, ones, tens, teens) => {
  if (n === 0) return "";
  if (n < 10) return ones[n];
  if (n < 20) return teens[n - 10];
  if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ` ${ones[n % 10]}` : "");
  return `${ones[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${convertLessThanThousand(n % 100, ones, tens, teens)}` : ""}`;
};

const buildWords = (num, includeInrPrefix = true) => {
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];

  const whole = Math.floor(parseFloat(num) || 0);
  if (whole === 0) return "Zero";

  const crore = Math.floor(whole / 10000000);
  const lakh = Math.floor((whole % 10000000) / 100000);
  const thousand = Math.floor((whole % 100000) / 1000);
  const remainder = whole % 1000;

  let result = "";
  if (crore) result += `${convertLessThanThousand(crore, ones, tens, teens)} Crore `;
  if (lakh) result += `${convertLessThanThousand(lakh, ones, tens, teens)} ${lakh > 1 ? "Lacs " : "Lac "}`;
  if (thousand) result += `${convertLessThanThousand(thousand, ones, tens, teens)} Thousand `;
  if (remainder) result += convertLessThanThousand(remainder, ones, tens, teens);

  const paise = Math.round(((parseFloat(num) || 0) - whole) * 100);
  const words = result.trim();
  if (paise > 0) {
    const paiseWords = convertLessThanThousand(paise, ones, tens, teens);
    return includeInrPrefix ? `INR ${words} and ${paiseWords} paise` : `${words} and ${paiseWords} paise`;
  }
  return includeInrPrefix ? `INR ${words}` : words;
};

export const numberToWords = (num) => buildWords(num, true);

export const formatAmountInWords = (num) => buildWords(num, false);

export const formatIndianNumber = (value, decimals = 2) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return (0).toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return num.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const enhanceLineItemsWithTax = (lineItems = [], doc = {}) => {
  const cgstPct = parseFloat(doc.cgst_percentage) || 0;
  const sgstPct = parseFloat(doc.sgst_percentage) || 0;
  const igstPct = parseFloat(doc.igst_percentage) || 0;
  const headerTaxable = parseFloat(doc.taxable_amount) || 0;
  const headerCgst = parseFloat(doc.cgst_amount) || 0;
  const headerSgst = parseFloat(doc.sgst_amount) || 0;
  const headerIgst = parseFloat(doc.igst_amount) || 0;

  return lineItems.map((item) => {
    const taxable = parseFloat(item.taxable_amount ?? item.amount) || 0;
    let cgstAmt = parseFloat(item.cgst_amount) || 0;
    let sgstAmt = parseFloat(item.sgst_amount) || 0;
    let igstAmt = parseFloat(item.igst_amount) || 0;

    if (!cgstAmt && !sgstAmt && !igstAmt && taxable > 0) {
      if (cgstPct || sgstPct || igstPct) {
        cgstAmt = (taxable * cgstPct) / 100;
        sgstAmt = (taxable * sgstPct) / 100;
        igstAmt = (taxable * igstPct) / 100;
      } else if (headerTaxable > 0) {
        const ratio = taxable / headerTaxable;
        cgstAmt = headerCgst * ratio;
        sgstAmt = headerSgst * ratio;
        igstAmt = headerIgst * ratio;
      }
    }

    return {
      ...item,
      taxable_amount: taxable,
      cgstAmt,
      sgstAmt,
      igstAmt,
      itemTotal: taxable + cgstAmt + sgstAmt + igstAmt,
    };
  });
};

export const buildHsnGroups = (enhancedItems = []) =>
  enhancedItems.reduce((acc, item) => {
    const hsnCode = item.hsn_code || item.hsn_sac || "997319";
    if (!acc[hsnCode]) {
      acc[hsnCode] = { taxable: 0, cgst: 0, sgst: 0, igst: 0 };
    }
    acc[hsnCode].taxable += parseFloat(item.taxable_amount) || 0;
    acc[hsnCode].cgst += parseFloat(item.cgstAmt) || 0;
    acc[hsnCode].sgst += parseFloat(item.sgstAmt) || 0;
    acc[hsnCode].igst += parseFloat(item.igstAmt) || 0;
    return acc;
  }, {});

export const deriveTaxRates = (doc = {}, enhancedItems = []) => {
  const cgstPct = parseFloat(doc.cgst_percentage) || 0;
  const sgstPct = parseFloat(doc.sgst_percentage) || 0;
  const igstPct = parseFloat(doc.igst_percentage) || 0;

  if (cgstPct || sgstPct || igstPct) {
    return { cgstSgstRate: cgstPct || sgstPct, igstRate: igstPct };
  }

  const taxable = enhancedItems.reduce((sum, item) => sum + (parseFloat(item.taxable_amount) || 0), 0);
  const cgst = parseFloat(doc.cgst_amount) || enhancedItems.reduce((sum, item) => sum + (parseFloat(item.cgstAmt) || 0), 0);
  const igst = parseFloat(doc.igst_amount) || enhancedItems.reduce((sum, item) => sum + (parseFloat(item.igstAmt) || 0), 0);

  return {
    cgstSgstRate: taxable > 0 && cgst > 0 ? Math.round((cgst / taxable) * 10000) / 100 : 9,
    igstRate: taxable > 0 && igst > 0 ? Math.round((igst / taxable) * 10000) / 100 : 18,
  };
};

export const DEFAULT_COMPANY = {
  name: "PUNE LOGITECH SERVICES PRIVATE LIMITED",
  address: "Gat No.299/3, Maan Road, Phase-3, Near Hinjewadi Metro Car Shed, Hinjewadi, Pune 411057",
  city: "Pune",
  state: "Maharashtra",
  state_code: "27",
  cin: "U74999PN2010PTC136878",
  udyam_no: "UDYAM-MH-26-0042345",
  gstin: "27AAPCP0240H1ZG",
  pan: "AAPCP0240H",
  email: "raju@punelogitech.in",
  phone: "+91-20-12345678",
  bank_name: "Kotak Mahindra Bank Ltd.",
  bank_account_name: "PUNE LOGITECH SERVICES PRIVATE LIMITED",
  bank_account_no: "8612345678",
  bank_branch: "Hinjewadi, Pune",
  bank_ifsc: "KKBK0001777",
};

export const sharedPdfStyles = {
  amountWordsSection: {
    borderWidth: 1,
    borderColor: "#000000",
    borderStyle: "solid",
    padding: 8,
    marginBottom: 3,
    fontSize: 9,
  },
  bankSection: {
    borderWidth: 1,
    borderColor: "#000000",
    borderStyle: "solid",
    padding: 8,
    marginBottom: 3,
    fontSize: 9,
  },
  termsDeliverySection: {
    borderWidth: 1,
    borderColor: "#000000",
    borderStyle: "solid",
    padding: 8,
    marginBottom: 3,
    fontSize: 8,
    lineHeight: 1.4,
  },
};
